import React, { useState, useEffect, useRef, useCallback } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  Avatar,
  useMediaQuery,
  useTheme,
  Stack,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";

// Updated to only include the pages you have
const NAV_LINKS = [
  { label: "About Us", path: "/AboutUs" },
  { label: "Solutions", path: "/#solutions" },
  { label: "Team", path: "/people" },
  { label: "Contact Us", path: "/contact" },
];

// For mobile menu - same links
const MOBILE_NAV_ITEMS = [
  { to: "/", label: "Home" },
  { to: "/AboutUs", label: "About Us" },
  { to: "/#solutions", label: "Solutions" },
  { to: "/people", label: "Team" },
  { to: "/contact", label: "Contact Us" },
];

interface HeaderProps {
  isDemoFormOpen?: boolean;
  setIsDemoFormOpen?: (open: boolean) => void;
}

const Header: React.FC<HeaderProps> = ({ isDemoFormOpen = false, setIsDemoFormOpen }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCloseButtonHovered, setIsCloseButtonHovered] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeMobileLink, setActiveMobileLink] = useState<string>("");
  const location = useLocation();
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const shouldHideNavLinks = isMobile;

  const isContactPage = location.pathname === "/contactUs";

  const toggleExpand = () => setIsExpanded(!isExpanded);
  const closeDropdown = () => setIsExpanded(false);

  const toggleSideNav = () => {
    setIsMenuOpen(!isMenuOpen);
    setIsCloseButtonHovered(false);
  };

  // Function to handle Solutions link click
 const handleSolutionsClick = (e: React.MouseEvent) => {
  e.preventDefault();

  const scrollToSolutions = () => {
    const solutionsElement = document.getElementById("solutions");
    if (solutionsElement) {
      const headerOffset = 90; 
      const elementPosition = solutionsElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;

      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };

  if (location.pathname === "/") {
    scrollToSolutions();
    window.history.pushState(null, "", "/#solutions");
  } else {
    navigate("/");
    setTimeout(scrollToSolutions, 400);
  }

  if (isMenuOpen) setIsMenuOpen(false);
};


  // Function to handle mobile navigation
const handleMobileNavClick = (to: string) => {
  setActiveMobileLink(to);

  const scrollToSolutions = () => {
    const el = document.getElementById("solutions");
    if (!el) return;

    const headerOffset = 100;
    const elementTop = el.getBoundingClientRect().top + window.scrollY;
    const offsetPosition = elementTop - headerOffset;

    window.scrollTo({ top: offsetPosition, behavior: "smooth" });
  };

  if (to === "/#solutions") {
    if (location.pathname === "/") {
      window.history.pushState(null, "", "/#solutions");
      scrollToSolutions();
    } else {
      navigate("/");
      setTimeout(() => {
        window.history.pushState(null, "", "/#solutions");
        scrollToSolutions();
      }, 300);
    }
  } 
  else {
    window.scrollTo({ top: 0 });
    navigate(to);
  }

  setIsMenuOpen(false);
};

  // Reset active mobile link when location changes
 useEffect(() => {
  if (location.hash !== "#solutions") {
    setActiveMobileLink("");
  }
}, [location.pathname, location.hash]);


  // Close side menu when demo form opens
  useEffect(() => {
    if (isDemoFormOpen) {
      setIsMenuOpen(false);
      setIsExpanded(false);
    }
  }, [isDemoFormOpen]);

  useEffect(() => {
    if (contentRef.current) setContentHeight(contentRef.current.scrollHeight);
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        event.target instanceof Node &&
        !dropdownRef.current.contains(event.target)
      ) {
        closeDropdown();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("no-scroll", isMenuOpen);
    return () => document.body.classList.remove("no-scroll");
  }, [isMenuOpen]);
  
  const handleMouseEnter = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const target = e.target as HTMLElement;
    target.style.color = "#f77707ff";
  }, []);

  const handleMouseLeave = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const target = e.target as HTMLElement;
    // Check if this is the active link
    const linkPath = NAV_LINKS.find(link => link.label === target.textContent)?.path;
    const isActive = linkPath === location.pathname || (linkPath === '/' && location.pathname === '/');
    target.style.color = isActive ? "black" : (isScrolled ? "black" : "white");
  }, [isScrolled, location.pathname]);

  const handleLogoClick = () => {
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    }
  };

  // Function to check if a link is active (for desktop)
  const isActiveLink = (linkPath: string) => {
    if (linkPath === '/#solutions') {
      return location.pathname === '/' && location.hash === '#solutions';
    }
    if (linkPath === "/") {
      return location.pathname === "/";
    }
    return location.pathname === linkPath;
  };

  // Function to check if mobile link is active
const isMobileLinkActive = (to: string) => {

  // Solutions tab active when hash is #solutions
  if (to === "/#solutions") {
    return location.pathname === "/" && location.hash === "#solutions";
  }

  // Home tab active only when exactly on home (no hash)
  if (to === "/") {
    return location.pathname === "/" && !location.hash;
  }

  // All other tabs
  return location.pathname === to;
};



  return (
    <Stack>
      <Box
        sx={{
          opacity: isDemoFormOpen ? 0.2 : 1,
          pointerEvents: isDemoFormOpen ? "none" : "auto",
          transition: "opacity 0.3s ease-in-out",
        }}
      >
        <Box
          sx={{
            display: isDemoFormOpen ? "none" : "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "fixed",
            top: 0,
            width: "100%",
            height: { xs: "70px", sm: "80px", md: "70px" },
            backgroundColor: isScrolled ? "#ffffff" : "transparent",
            transition: "all 0.3s ease",
            zIndex: 9999,
            px: { xs: 1, sm: 2, md: 2 },
            boxShadow: isScrolled ? "0 2px 10px rgba(0,0,0,0.1)" : "none",
          }}
        >
          {/* Logo */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box onClick={handleLogoClick} sx={{ cursor: "pointer" }}>
              <Avatar
                src="https://d357a0xuzx4dll.cloudfront.net/gcrs-website/gcrs_images/GCRSLOgo.svg"
                alt="Logo"
                sx={{ 
                  width: { xs: 79, sm: 100, md: 120, lg: 150 }, 
                  height: "auto", 
                  borderRadius: 0,
                }}
              />
            </Box>
          </Box>

          {/* Navigation Links - Hidden on mobile and tablet */}
          {!shouldHideNavLinks && !isMenuOpen && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                gap: { md: 1, lg: 1, xl: 1 },
                borderRadius: 5,
                px: { md: 1, lg: 3, xl: 4 },
                py: { md: 0.75, lg: 1, xl: 1 },
                height: { md: "48px", lg: "54px", xl: "50px" },
                fontSize: { md: "14px", lg: "15px", xl: "16px" },
                fontFamily: `"system-ui", "-apple-system", "Segoe UI", "Roboto", "Helvetica Neue", "Arial", sans-serif"`,
                width: "fit-content",
                mx: { md: 1, lg: 2, xl: 6 },
                backgroundColor: isScrolled ? "rgba(255, 255, 255, 0.9)" : "rgba(255, 255, 255, 0.2)",
                backdropFilter: isScrolled ? "none" : "blur(10px)",
              }}
            >
              {NAV_LINKS.map((link) => {
                const isActive = isActiveLink(link.path);
                
                // Special handling for Solutions link
                if (link.path === '/#solutions') {
                  return (
                    <Box
                      key={link.path}
                      component="button"
                      onClick={handleSolutionsClick}
                      sx={{
                        fontSize: "inherit",
                        textDecoration: "none",
                        padding: "6px 8px",
                        color: isActive ? "black" : (isScrolled ? "black" : "white"),
                        fontWeight: isActive ? "600" : "400",
                        borderBottom: isActive ? "2px solid black" : "none",
                        transition: "all 0.3s ease",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        "&:hover": {
                          color: "#f77707ff",
                        }
                      }}
                    >
                      {link.label}
                    </Box>
                  );
                }
                
                // Regular links for other pages
                return (
                  <NavLink
                    key={link.path}
                    to={link.path}
                    style={{
                      fontSize: "inherit",
                      textDecoration: "none",
                      padding: "6px 8px",
                      color: isActive ? "black" : (isScrolled ? "black" : "white"),
                      fontWeight: isActive ? "600" : "400",
                      borderBottom: isActive ? "2px solid black" : "none",
                      transition: "all 0.3s ease",
                    }}
                    onMouseOver={handleMouseEnter}
                    onMouseOut={handleMouseLeave}
                  >
                    {link.label}
                  </NavLink>
                );
              })}
            </Box>
          )}

          {/* Hamburger Menu - Show on mobile and tablet */}
          {!isDemoFormOpen && shouldHideNavLinks && (
            <IconButton
              onClick={toggleSideNav}
              sx={{
                border: `1.5px solid ${isScrolled ? "black" : "black"}`,
                borderRadius: { xs: "6px", sm: "8px", md: "12px" },
                padding: { xs: "4px", sm: "6px", md: "8px" },
                backgroundColor: "transparent",
                mt: { xs: "6px", sm: "0px" },
                mr: {xs:2, sm:3},
                "&:hover": { backgroundColor: isScrolled ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)" },
              }}
            >
              <MenuIcon 
                sx={{ 
                  color: isScrolled ? "black" : "black",
                  fontSize: { xs: "20px", sm: "22px", md: "24px" }
                }} 
              />
            </IconButton>
          )}
        </Box>
        {/* End of Header Main Box */}

        {/* Gray Divider - Show when scrolled */}
        {isScrolled && (
          <Box
            sx={{
              height: "1px",
              backgroundColor: "#e0e0e0",
              width: "100%",
              position: "fixed",
              top: { xs: "70px", sm: "80px", md: "70px" },
              zIndex: 9998,
            }}
          />
        )}

        {/* Side Navigation Drawer */}
        {isMenuOpen && !isDemoFormOpen && (
          <>
            {/* Backdrop */}
            <Box
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                zIndex: 9998,
              }}
              onClick={toggleSideNav}
            />

            {/* Side Navigation */}
            <Box
              sx={{
                position: "fixed",
                top: 0,
                right: 0,
                width: { xs: "55%", sm: "40%", md: "40%", lg: "30%", xl: "25%" },
                height: "100%",
                backgroundColor: "#fff",
                transition: "right 0.5s ease-in-out",
                boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.3)",
                zIndex: 9999,
                display: "flex",
                flexDirection: "column",
                overflowY: "auto",
                padding: { xs: "14px 8px", sm: "10px 8px", md: "20px 14px" },
                '&::-webkit-scrollbar': {
                  display: 'none'
                },
                scrollbarWidth: 'none',
                msOverflowStyle: 'none', 
              }}
            >
              {/* Close Button */}
              <IconButton
                onClick={toggleSideNav}
                sx={{
                  position: "absolute",
                  top: { xs: 12, sm: 14, md: 16 },
                  right: { xs: 12, sm: 14, md: 16 },
                  padding: { xs: "6px", sm: "7px", md: "8px" },
                  zIndex: 10000,
                  backgroundColor: "#fff",
                  borderRadius: "6px",
                  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                  border: "1px solid #ccc",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
                onMouseEnter={() => setIsCloseButtonHovered(true)}
                onMouseLeave={() => setIsCloseButtonHovered(false)}
              >
                <CloseIcon 
                  sx={{ 
                    fontSize: { xs: "18px", sm: "20px", md: "22px" },
                    color: "#333"
                  }} 
                />
              </IconButton>

              {/* Navigation Section */}
              <List
                sx={{
                  mt: { xs: "28px", sm: "32px", md: "40px" },
                  display: "flex",
                  flexDirection: "column",
                  gap: "3px",
                  fontFamily: "system-ui, -apple-system, sans-serif",
                }}
              >
                {MOBILE_NAV_ITEMS.map((item, index) => {
                  const isActive = isMobileLinkActive(item.to);
                  return (
                    <ListItem key={index} sx={{ padding: 0 }}>
                      <Box
                        component="button"
                        onClick={() => handleMobileNavClick(item.to)}
                        sx={{
                          fontSize: { xs: "18px", sm: "20px", md: "24px" },
                          fontWeight: isActive ? "600" : "bold",
                          color: isActive ? "black" : "black",
                          textDecoration: isActive ? "underline" : "none",
                          textDecorationColor: isActive ? "black" : "none",
                          textUnderlineOffset: "4px",
                          margin: "0px 0px 8px",
                          transition: "all 0.2s ease",
                          background: "none",
                          border: "none",
                          textAlign: "left",
                          cursor: "pointer",
                          width: "100%",
                          "&:hover": {
                            color: "#f77707ff",
                            textDecoration: "underline",
                          },
                        }}
                      >
                        {item.label}
                      </Box>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          </>
        )}
      </Box>
    </Stack>
  );
};

export default Header;